/**
 * Timer de phase pilote par le serveur.
 *
 * Il ne compte JAMAIS une duree locale : il lit `endsAt` (horodatage serveur) et
 * l'offset d'horloge mesure a la connexion. C'est ce qui garantit que deux
 * onglets affichent la meme valeur, meme si leurs horloges systeme divergent.
 *
 * Comme TimerRing, tout se joue dans une boucle rAF qui ecrit dans le DOM :
 * aucun setState a 60 fps.
 *
 * Deux mecanismes d'expiration cohabitent : la boucle rAF, et un setTimeout cale
 * sur l'echeance, parce que le navigateur suspend rAF dans un onglet cache.
 *
 * Ni l'un ni l'autre n'est une GARANTIE : Chrome bride aussi les minuteurs des
 * onglets d'arriere-plan, jusqu'a plusieurs dizaines de secondes de retard. Ce
 * qui garantit la soumission d'un dessin, c'est le changement de phase envoye
 * par le serveur (cf. DrawingScreen) : un message WebSocket, lui, arrive
 * toujours. Ce timer ne sert qu'a l'affichage et au cas nominal.
 */
import { useEffect, useRef, type ReactElement } from 'react';
import { connection } from '../net/connection.js';
import { playSound } from '../audio/sounds.js';

export interface PhaseTimerProps {
  /** Horodatage serveur de fin de phase. */
  readonly endsAt: number | null;
  /** Duree totale de la phase, pour l'anneau de progression. */
  readonly durationMs: number;
  readonly size?: number;
  readonly onExpire?: () => void;
  /**
   * Debut de l'alerte, en SECONDES restantes. Un seuil en fraction de la duree
   * donnait 24 s d'alerte sur une phase de 120 s et 6 s sur une phase de 30 s :
   * la meme urgence doit se declencher au meme moment reel.
   */
  readonly dangerSeconds?: number;
  /** Emettre un tic a chaque seconde de l'alerte. */
  readonly tick?: boolean;
}

const STROKE_WIDTH = 4;

export function PhaseTimer({
  endsAt,
  durationMs,
  size = 56,
  onExpire,
  dangerSeconds = 10,
  tick = false,
}: PhaseTimerProps): ReactElement {
  const rootRef = useRef<SVGSVGElement | null>(null);
  const trackRef = useRef<SVGCircleElement | null>(null);
  const labelRef = useRef<SVGTextElement | null>(null);
  const expireRef = useRef(onExpire);
  expireRef.current = onExpire;

  const radius = (size - STROKE_WIDTH) / 2;
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    if (endsAt === null) return;

    let raf = 0;
    let fired = false;
    let lastSecond = -1;
    let lastDanger: boolean | null = null;

    const expire = (): void => {
      if (fired) return;
      fired = true;
      expireRef.current?.();
    };

    const frame = (): void => {
      const remaining = connection.remaining(endsAt);
      const ratio = durationMs > 0 ? Math.min(remaining / durationMs, 1) : 0;

      trackRef.current?.setAttribute('stroke-dashoffset', String(circumference * (1 - ratio)));

      const seconds = Math.ceil(remaining / 1000);
      const danger = remaining <= dangerSeconds * 1000;

      if (seconds !== lastSecond) {
        if (labelRef.current !== null) labelRef.current.textContent = String(seconds);
        // Un tic par seconde pendant l'alerte, jamais a l'expiration : le son de
        // fin de phase a le sien.
        if (tick && danger && seconds > 0 && lastSecond !== -1) playSound('tick');
        lastSecond = seconds;
      }

      if (danger !== lastDanger && rootRef.current !== null) {
        rootRef.current.dataset['danger'] = danger ? 'true' : 'false';
        lastDanger = danger;
      }

      if (remaining <= 0) {
        expire();
        return;
      }
      raf = requestAnimationFrame(frame);
    };

    // Filet d'arriere-plan : le rAF ci-dessus ne tourne pas dans un onglet cache.
    const guard = window.setTimeout(expire, Math.max(connection.remaining(endsAt), 0) + 250);

    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(guard);
    };
  }, [endsAt, durationMs, circumference, dangerSeconds, tick]);

  const center = size / 2;

  // Le compte a rebours n'est PAS annonce a chaque seconde (aria-live="off") :
  // ce serait un flux continu inecoutable. Le chiffre reste visible a l'ecran,
  // et les changements de phase passent par les toasts et les bandeaux.
  return (
    <svg
      ref={rootRef}
      className="timer-ring"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      data-danger="false"
      role="timer"
      aria-live="off"
    >
      <circle cx={center} cy={center} r={radius} fill="none" stroke="var(--border-2)" strokeWidth={STROKE_WIDTH} />
      <circle
        ref={trackRef}
        className="timer-ring__progress"
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={0}
        transform={`rotate(-90 ${center} ${center})`}
      />
      <text
        ref={labelRef}
        className="timer-ring__label mono-tabular"
        x={center}
        y={center}
        textAnchor="middle"
        dominantBaseline="central"
      />
    </svg>
  );
}
